import { useQuery } from '@tanstack/react-query';
import { useLocalizeMessage, useToastNotification } from 'libs.nucleus';
import { SITE_DOCUMENT_DETAIL_QUERY_KEY, SITE_DOCUMENT_INVITES_QUERY_KEY, } from '../site_document_details.types';
/**
 * `ec__document_template` is a Reference, so dotted paths alone come back as a bare
 * `{ _id, object, path }` stub. It needs `expand` as well — and `expand` on its own
 * returns the entire template including `ec__html` and signed asset URLs, so the two
 * are paired to keep the payload to the fields the page renders.
 *
 * `ec__published_studio_template` is a Reference on the same terms, and it is where a
 * Studio-published consent's signer roles live.
 */
export const SITE_DOCUMENT_EXPAND = ['ec__document_template', 'ec__published_studio_template'];
export const SITE_DOCUMENT_DETAIL_PATHS = [
    '_id',
    'created',
    'ec__identifier',
    'ec__primary_participant.c_number',
    'ec__status',
    'ec__pts',
    'ec__completion_time',
    'ec__cancellation_reason',
    'ec__void_context.ec__cause',
    'ec__void_context.ec__source',
    'ec__void_context.ec__at',
    'ec__void_context.ec__template_version',
    'ec__void_context.ec__actor_name',
    'ec__void_context.ec__actor_id',
    'ec__can_sign_document',
    'ec__pts_signature_date_wallclock',
    'ec__final_document',
    'ec__document_template._id',
    'ec__document_template.ec__title',
    'ec__document_template.ec__pts_only',
    'ec__document_template.ec__delayed_signing',
    'ec__document_template.ec__signer_roles',
    'ec__document_template.ec__pts_document',
    'ec__document_template.ec__requested_data',
    'ec__published_studio_template.ec__bundle',
    'ec__published_studio_template.ec__requested_signatures',
    'ec__published_studio_template.ec__requested_data',
    'ec__published_studio_template.ec__signer_roles',
    // Whole subdocuments (identifier + value). Download stamps dates and free-text
    // from these rows; omitting them left the site copy with signatures only.
    'ec__required_data',
    'ec__signatures',
    'ec__accepted_signers.ec__signer_role',
    'ec__accepted_signers.ec__status',
    'ec__accepted_signers.ec__completion_time',
];
export const SITE_DOCUMENT_INVITE_PATHS = ['_id', 'ec__email', 'ec__order', 'ec__signer_role', 'ec__status'];
export const useSiteDocument = ({ accountId, documentId, apiClient, getApiErrorMessage, }) => {
    const { addNotification } = useToastNotification();
    const translate = useLocalizeMessage();
    const basePath = `/v2/account/${accountId}/ec__site_documents/${documentId}`;
    const isEnabled = !!accountId && !!documentId;
    const notifyError = (error) => {
        addNotification({
            title: '',
            subtitle: getApiErrorMessage?.(error) ?? translate('error.participant_documents.load_failed'),
            type: 'error',
        });
    };
    const documentQuery = useQuery({
        queryKey: [SITE_DOCUMENT_DETAIL_QUERY_KEY, accountId, documentId],
        enabled: isEnabled,
        queryFn: async () => {
            try {
                const response = await apiClient.get(basePath, {
                    params: {
                        expand: SITE_DOCUMENT_EXPAND,
                        paths: SITE_DOCUMENT_DETAIL_PATHS,
                        passive: true,
                    },
                });
                return response?.data;
            }
            catch (error) {
                notifyError(error);
                throw error;
            }
        },
    });
    const invitesQuery = useQuery({
        queryKey: [SITE_DOCUMENT_INVITES_QUERY_KEY, accountId, documentId],
        enabled: isEnabled,
        queryFn: async () => {
            try {
                const response = await apiClient.get(`${basePath}/ec__signature_invites`, {
                    params: {
                        paths: SITE_DOCUMENT_INVITE_PATHS,
                        passive: true,
                    },
                });
                return response?.data?.data ?? [];
            }
            catch (error) {
                notifyError(error);
                throw error;
            }
        },
    });
    return {
        document: documentQuery.data,
        invites: invitesQuery.data ?? [],
        isLoading: documentQuery.isLoading || invitesQuery.isLoading,
        areInvitesUnavailable: invitesQuery.isError,
    };
};
